import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { FilterBar } from '../../components/FilterBar';
import api from '../../config/api';

interface Contact {
  id: number;
  nom: string;
  email: string;
  sujet: string;
  message: string;
  telephone?: string;
  lu: boolean;
  repondu: boolean;
  confirmation_sent: boolean;
  created_at: string;
  updated_at: string;
}

interface ContactStats {
  total: number;
  lus: number;
  non_lus: number;
  repondus: number;
  non_repondus: number;
}

export const AdminContact: React.FC = () => {
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<ContactStats>({
    total: 0,
    lus: 0,
    non_lus: 0,
    repondus: 0,
    non_repondus: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    lu: '',
    repondu: '',
  });

  useEffect(() => {
    fetchContacts(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allContacts, currentPage]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/contact/contacts/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchContacts = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/contact/contacts/?page_size=1000`);
      const allContactsData = response.data.results || [];
      setAllContacts(allContactsData);
      setTotalCount(allContactsData.length);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allContacts];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((contact) =>
        contact.nom?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.sujet?.toLowerCase().includes(searchLower) ||
        contact.message?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par lu/non lu
    if (filters.lu) {
      const isLu = filters.lu === 'lu';
      filtered = filtered.filter((contact) => contact.lu === isLu);
    }

    // Filtre par répondu/non répondu
    if (filters.repondu) {
      const isRepondu = filters.repondu === 'repondu';
      filtered = filtered.filter((contact) => contact.repondu === isRepondu);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedContacts = filtered.slice(startIndex, endIndex);

    setContacts(paginatedContacts.map(formatData) as unknown as Contact[]);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleView = (contact: Contact) => {
    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const handleMarkAsRead = async (contact: Contact) => {
    try {
      await api.patch(`/contact/contacts/${contact.id}/`, { lu: true });
      await fetchContacts(currentPage);
      fetchStats();
      alert('Message marqué comme lu');
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || error.response?.data?.error || 'Erreur lors du marquage comme lu');
    }
  };

  const handleMarkAsAnswered = async (contact: Contact) => {
    try {
      await api.patch(`/contact/contacts/${contact.id}/`, { repondu: true });
      await fetchContacts(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      return;
    }
    try {
      await api.delete(`/contact/contacts/${id}/`);
      await fetchContacts(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleReply = (contact: Contact) => {
    setSelectedContact(contact);
    setReplyText('');
    setIsReplyModalOpen(true);
  };

  const handleSendReply = async () => {
    if (!selectedContact || !replyText.trim()) {
      alert('Veuillez saisir une réponse');
      return;
    }

    setSendingReply(true);
    try {
      await api.post(`/contact/contacts/${selectedContact.id}/reply/`, {
        reponse: replyText,
      });
      setIsReplyModalOpen(false);
      setReplyText('');
      await fetchContacts(currentPage);
      fetchStats();
      alert('Réponse envoyée avec succès !');
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'envoi de la réponse');
    } finally {
      setSendingReply(false);
    }
  };

  const columns = [
    { key: 'nom', label: 'Nom' },
    { key: 'email', label: 'Email' },
    { key: 'sujet', label: 'Sujet' },
    { 
      key: 'lu', 
      label: 'Lu',
      render: (_: any, row: Contact) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleMarkAsRead(row);
          }}
          disabled={row.lu}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            row.lu
              ? 'bg-green-100 text-green-800 cursor-default'
              : 'bg-orange-100 text-orange-800 hover:bg-orange-200 cursor-pointer'
          }`}
        >
          {row.lu ? '✅ Lu' : '📧 Marquer comme lu'}
        </button>
      )
    },
    { key: 'repondu', label: 'Répondu' },
    { key: 'created_at', label: 'Date' },
  ];

  const formatData = (contact: Contact) => ({
    ...contact,
    repondu: contact.repondu ? '✅ Oui' : '❌ Non',
    created_at: new Date(contact.created_at).toLocaleDateString('fr-FR'),
  });

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Messages de Contact</h1>
        </div>

        {/* Filtres */}
        <FilterBar
          searchPlaceholder="🔍 Rechercher par nom, email, sujet..."
          searchValue={filters.search}
          onSearchChange={(value) => {
            setFilters({ ...filters, search: value });
            setCurrentPage(1);
          }}
          filters={[
            {
              key: 'lu',
              label: 'Tous (lu/non lu)',
              value: filters.lu,
              onChange: (value) => {
                setFilters({ ...filters, lu: value });
                setCurrentPage(1);
              },
              options: [
                { value: 'lu', label: '✅ Lus' },
                { value: 'non_lu', label: '❌ Non lus' },
              ],
            },
            {
              key: 'repondu',
              label: 'Tous (répondu/non répondu)',
              value: filters.repondu,
              onChange: (value) => {
                setFilters({ ...filters, repondu: value });
                setCurrentPage(1);
              },
              options: [
                { value: 'repondu', label: '✅ Répondus' },
                { value: 'non_repondu', label: '❌ Non répondus' },
              ],
            },
          ]}
          onClear={() => {
            setFilters({ search: '', lu: '', repondu: '' });
            setCurrentPage(1);
          }}
        />

        {/* Cartes de statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Total Messages</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <span className="text-4xl opacity-80">📬</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Lus</p>
                <p className="text-3xl font-bold">{stats.lus}</p>
              </div>
              <span className="text-4xl opacity-80">✅</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm mb-1">Non lus</p>
                <p className="text-3xl font-bold">{stats.non_lus}</p>
              </div>
              <span className="text-4xl opacity-80">📧</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm mb-1">Répondus</p>
                <p className="text-3xl font-bold">{stats.repondus}</p>
              </div>
              <span className="text-4xl opacity-80">✉️</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm mb-1">En attente</p>
                <p className="text-3xl font-bold">{stats.non_repondus}</p>
              </div>
              <span className="text-4xl opacity-80">⏳</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <Table
                columns={columns}
                data={contacts}
                onView={handleView}
                onDelete={handleDelete}
              />
            </div>

            {totalPages > 1 && (
              <div className="mt-6">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    loading={loading}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
              </div>
            )}
          </>
        )}

        {/* Modal de visualisation */}
        {isViewModalOpen && selectedContact && (
          <Modal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            title={`Message de ${selectedContact.nom}`}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-gray-900">{selectedContact.email}</p>
              </div>
              {selectedContact.telephone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <p className="text-gray-900">{selectedContact.telephone}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                <p className="text-gray-900 font-semibold">{selectedContact.sujet}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <p className="text-gray-900 whitespace-pre-wrap break-words">{selectedContact.message}</p>
              </div>
              <div className="flex items-center space-x-4 pt-4 border-t">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedContact.lu ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedContact.lu ? '✅ Lu' : '❌ Non lu'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedContact.repondu ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedContact.repondu ? '✅ Répondu' : '❌ Non répondu'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedContact.confirmation_sent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedContact.confirmation_sent ? '✅ Email envoyé' : '❌ Email non envoyé'}
                </span>
              </div>
              <div className="flex items-center space-x-4 pt-4 border-t">
                <button
                  onClick={() => {
                    handleMarkAsRead(selectedContact);
                    setIsViewModalOpen(false);
                  }}
                  className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                    selectedContact.lu
                      ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                  disabled={selectedContact.lu}
                >
                  {selectedContact.lu ? '✅ Déjà lu' : '📬 Marquer comme lu'}
                </button>
                <button
                  onClick={() => {
                    handleReply(selectedContact);
                    setIsViewModalOpen(false);
                  }}
                  className="px-4 py-2 rounded-xl font-semibold transition-colors bg-blue-600 hover:bg-blue-700 text-white"
                >
                  ✉️ Répondre
                </button>
              </div>
              <div className="text-sm text-gray-500 pt-4">
                Reçu le {new Date(selectedContact.created_at).toLocaleString('fr-FR')}
              </div>
            </div>
          </Modal>
        )}

        {/* Modal de réponse */}
        {isReplyModalOpen && selectedContact && (
          <Modal
            isOpen={isReplyModalOpen}
            onClose={() => {
              setIsReplyModalOpen(false);
              setReplyText('');
            }}
            title={`Répondre à ${selectedContact.nom}`}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                <p className="text-gray-900 font-semibold">{selectedContact.sujet}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message original</label>
                <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg text-sm">
                  {selectedContact.message}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Votre réponse *</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white resize-none"
                  placeholder="Tapez votre réponse ici..."
                  required
                />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  onClick={() => {
                    setIsReplyModalOpen(false);
                    setReplyText('');
                  }}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sendingReply}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingReply ? 'Envoi...' : '✉️ Envoyer la réponse'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
};

